import React, { useState, useEffect, useCallback } from 'react'
import { Info, RefreshCw } from 'lucide-react'
import { DateRangePicker } from '../ui/DateRangePicker'
import { systemDashboardService, SystemTenant } from '../../services/systemDashboardService'
import scoutService, { ContentPost } from '../../services/scoutService'
import { socialMediaService } from '../../services/socialMediaService'
import { Cell } from '../content-performance/mockData'
import ContentHeatmap, { RawCell } from '../content-performance/ContentHeatmap'
import CategoryDetailView from '../content-performance/CategoryDetailView'
import TenantCategoryView from './TenantCategoryView'

/**
 * System Dashboard → Portfolio Value: the Content Category × format heatmap
 * aggregated ACROSS all active tenants (or one selected tenant), filtered by period.
 * Reuses the ContentHeatmap + CategoryDetailView components; data comes from the
 * cross-tenant /admin/system-dashboard/content-performance* endpoints.
 */
const SystemContentPerformance: React.FC = () => {
  const [tenants, setTenants] = useState<SystemTenant[]>([])
  const [tenant, setTenant] = useState('') // '' = all active tenants
  const [period, setPeriod] = useState('month')
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null)
  const [raw, setRaw] = useState<RawCell[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Drill-down levels: cell → tenants (catDetail) → that tenant's posts (tenantPost).
  const [catDetail, setCatDetail] = useState<{ categoryKey: string; categoryLabel: string; format: string; cell: Cell } | null>(null)
  const [tenantPost, setTenantPost] = useState<{ customerId: string; customerName: string } | null>(null)

  useEffect(() => {
    systemDashboardService.getTenants().then((r) => setTenants(r.data || [])).catch(() => {})
  }, [])

  // Picker's "Last 30 Days" preset → keyword 'month' (= calendar MTD on the BE); translate to
  // '30days' so the window is a rolling 30 days, matching the rest of content performance.
  const apiPeriod = period === 'custom' ? undefined : (period === 'month' ? '30days' : period)
  const customStart = period === 'custom' && dateRange ? dateRange.start : undefined
  const customEnd = period === 'custom' && dateRange ? dateRange.end : undefined
  const filters = { customerId: tenant || undefined, period: apiPeriod, customStartDate: customStart, customEndDate: customEnd }

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await systemDashboardService.getContentPerformance(filters)
      setRaw(res?.data ?? [])
    } catch {
      setError('Failed to load content performance'); setRaw([])
    } finally {
      setLoading(false)
    }
  }, [tenant, apiPeriod, customStart, customEnd]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load() }, [load])

  // Level 2: a specific tenant's posts within the clicked cell.
  if (catDetail && tenantPost) {
    return (
      <CategoryDetailView
        categoryLabel={`${tenantPost.customerName} — ${catDetail.categoryLabel}`}
        format={catDetail.format}
        cell={catDetail.cell}
        subtitle="Posts for this tenant in this category & format. Click a post to see the brands detected in it."
        backLabel="Back to tenants"
        fetchPosts={async () => (await systemDashboardService.getContentPerformancePosts({
          customerId: tenantPost.customerId, contentCategory: catDetail.categoryKey, format: catDetail.format,
          period: apiPeriod, customStartDate: customStart, customEndDate: customEnd,
        })).data ?? []}
        fetchPostBrands={async (post: ContentPost) => post.post_id != null && post.partition_key != null ? (await systemDashboardService.getPostBrands(post.customer_id || '', post.post_id, post.partition_key)).data ?? [] : []}
        thumbnailUrl={(post: ContentPost) => post.thumbnail_blob_path && post.customer_id
          ? socialMediaService.getThumbnailUrl(post.social_post_id, post.customer_id)
          : post.post_id != null && post.customer_id ? scoutService.getThumbnailUrl(post.post_id, post.customer_id) : null}
        onBack={() => setTenantPost(null)}
      />
    )
  }

  // Level 1: tenants that have content in the clicked cell.
  if (catDetail) {
    return (
      <TenantCategoryView
        categoryLabel={catDetail.categoryLabel}
        format={catDetail.format}
        cell={catDetail.cell}
        fetchTenants={async () => (await systemDashboardService.getContentPerformanceTenants({
          customerId: tenant || undefined, contentCategory: catDetail.categoryKey, format: catDetail.format,
          period: apiPeriod, customStartDate: customStart, customEndDate: customEnd,
        })).data ?? []}
        onTenantClick={(customerId, customerName) => setTenantPost({ customerId, customerName })}
        onBack={() => setCatDetail(null)}
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Portfolio Value</h2>
          <p className="text-sm text-gray-500">Content Category × format across active tenants — shaded by total EMV. Click a cell to drill in.</p>
        </div>
        <button onClick={load} className="p-1.5 hover:bg-gray-100 rounded-lg" title="Refresh">
          <RefreshCw className={`h-4 w-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs">
        <Info className="h-3.5 w-3.5 flex-shrink-0" />
        Live data for owned accounts across active tenants (Brand Scout).
      </div>

      {/* Filter bar (right-aligned) */}
      <div className="flex flex-wrap items-center justify-end gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <select value={tenant} onChange={(e) => setTenant(e.target.value)} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 max-w-[220px]">
          <option value="">All active tenants</option>
          {tenants.map((t) => <option key={t.customerId} value={t.customerId}>{t.name}</option>)}
        </select>
        <DateRangePicker value={dateRange} selectedPeriod={period} maxRetentionDays={365} onChange={(range, p) => { setPeriod(p); setDateRange(range) }} />
      </div>

      <ContentHeatmap cells={raw} loading={loading} error={error} onCellClick={(categoryKey, categoryLabel, format, cell) => { setTenantPost(null); setCatDetail({ categoryKey, categoryLabel, format, cell }) }} />
    </div>
  )
}

export default SystemContentPerformance
